import { useEffect, useState } from "react";
import { Send, Mail, Phone, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";

const TYPES = [
    { value: "pedagogique", label: "Pédagogique (contenu, formateur, supports)" },
    { value: "organisationnelle", label: "Organisationnelle (lieu, accueil, planning)" },
    { value: "facturation", label: "Facturation / financement" },
    { value: "autre", label: "Autre" },
];

export default function Reclamation() {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", type: "pedagogique", session_id: "", description: "",
    });
    const [sessions, setSessions] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        api.get("/sessions?limit=50").then((r) => setSessions(r.data || [])).catch(() => {});
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.description.length < 20) {
            toast.error("Merci de détailler votre réclamation (minimum 20 caractères).");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/reclamations", {
                ...form,
                session_id: form.session_id || null,
                phone: form.phone || null,
            });
            setSuccess(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-white">
            <SiteNav />
            <section className="relative border-b border-brand-line bg-brand-coal px-6 pt-32 pb-20 text-white">
                <div className="mx-auto max-w-5xl">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-orange">
                        Démarche qualité
                    </div>
                    <h1 className="mt-4 font-display text-6xl font-normal leading-[1.05] tracking-tightest">
                        Une remarque, une <span className="text-brand-orange">réclamation</span> ?
                    </h1>
                    <p className="mt-6 max-w-2xl text-lg text-white/70">
                        Toute réclamation est traitée sous <strong className="text-white">5 jours ouvrés</strong>.
                        Notre engagement Qualiopi : transparence totale, suivi nominatif, et amélioration continue.
                    </p>
                </div>
            </section>

            <section className="px-6 py-20">
                <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-3">
                    <div className="space-y-5 lg:col-span-1">
                        <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium">
                            <ShieldCheck className="h-8 w-8 text-brand-orange" />
                            <h3 className="mt-4 font-display text-xl">Confidentialité</h3>
                            <p className="mt-2 text-sm text-brand-smoke">
                                Vos données sont traitées exclusivement par notre référent qualité. Aucune diffusion à des tiers.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium">
                            <Mail className="h-8 w-8 text-brand-orange" />
                            <h3 className="mt-4 font-display text-xl">Contact direct</h3>
                            <a href="mailto:qualite@cursus-physio.fr" className="mt-2 inline-block text-sm text-brand-ink hover:text-brand-orange" data-testid="reclamation-direct-email">
                                qualite@cursus-physio.fr
                            </a>
                            <div className="mt-1 inline-flex items-center gap-2 text-sm text-brand-smoke">
                                <Phone className="h-3.5 w-3.5" /> +33 1 86 76 00 00
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        {success ? (
                            <div className="rounded-3xl border border-green-200 bg-green-50 p-10 text-center" data-testid="reclamation-success">
                                <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700">
                                    <ShieldCheck className="h-7 w-7" />
                                </div>
                                <h2 className="mt-6 font-display text-3xl text-brand-ink">Réclamation enregistrée</h2>
                                <p className="mt-3 text-brand-smoke">{success.message}</p>
                                <p className="mt-2 text-xs text-brand-smoke">Numéro de référence : <code className="font-semibold text-brand-ink">{success.id.slice(0, 8)}</code></p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="rounded-3xl border border-brand-line bg-white p-8 shadow-premium space-y-5" data-testid="reclamation-form">
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Nom complet *</label>
                                        <input
                                            required
                                            value={form.name}
                                            onChange={(e) => set("name", e.target.value)}
                                            data-testid="reclamation-name"
                                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Email *</label>
                                        <input
                                            type="email"
                                            required
                                            value={form.email}
                                            onChange={(e) => set("email", e.target.value)}
                                            data-testid="reclamation-email"
                                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                                        />
                                    </div>
                                </div>
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Téléphone (optionnel)</label>
                                        <input
                                            value={form.phone}
                                            onChange={(e) => set("phone", e.target.value)}
                                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Type de réclamation *</label>
                                        <select
                                            value={form.type}
                                            onChange={(e) => set("type", e.target.value)}
                                            data-testid="reclamation-type"
                                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                                        >
                                            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Session concernée (optionnel)</label>
                                    <select
                                        value={form.session_id}
                                        onChange={(e) => set("session_id", e.target.value)}
                                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                                    >
                                        <option value="">— Aucune session précise —</option>
                                        {sessions.map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {s.formation_name} · {s.start_date} · {s.city}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                                        Description de votre réclamation * <span className="text-brand-smoke/60 normal-case">({form.description.length}/4000)</span>
                                    </label>
                                    <textarea
                                        required
                                        rows={6}
                                        value={form.description}
                                        onChange={(e) => set("description", e.target.value)}
                                        placeholder="Décrivez précisément les faits, dates, personnes concernées si pertinent…"
                                        data-testid="reclamation-description"
                                        className="mt-2 w-full resize-none rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange"
                                    />
                                </div>
                                <div className="flex items-start gap-2 rounded-xl bg-orange-50 p-4 text-xs text-brand-ink/80">
                                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" />
                                    <span>
                                        Votre réclamation est confidentielle et traitée par notre référent qualité.
                                        Une réponse vous sera adressée par email sous 5 jours ouvrés.
                                    </span>
                                </div>
                                <div className="flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="reclamation-submit"
                                        className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-3 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60"
                                    >
                                        <Send className="h-3.5 w-3.5" />
                                        {submitting ? "Envoi…" : "Envoyer ma réclamation"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
